<template>
  <div class="dashboard">
    <!-- Mensagem se nenhum tipo selecionado -->
    <v-row v-if="selectedProjectType === null" class="mb-2">
      <v-col cols="12">
        <v-alert type="info" variant="tonal" density="compact" class="mb-0">
          {{ $t('dashboard.selectType') }}
        </v-alert>
      </v-col>
    </v-row>
    <!-- Header do Dashboard -->
    <v-toolbar density="compact" color="" class="mb-2">
      <v-btn-toggle
        :model-value="selectedProjectType"
        @update:model-value="selectProjectType"
        :mandatory="selectedProjectType !== null"
        class="d-flex flex-grow-1"
        color="primary"
        variant="outlined"
        divided
      >
        <v-btn
          v-for="type in projectTypeSummary"
          :key="type.id"
          :value="type.id"
          class="flex-grow-1"
        >
          <span class="mr-2 text-h5">{{ type.icon }}</span>
          <span class="font-weight-bold">{{ type.descr }}</span>
          <v-chip
            size="small"
            :color="selectedProjectType === type.id ? 'primary' : 'grey'"
            class="ml-2 font-weight-bold"
            variant="flat"
          >
            {{ type.count }}
          </v-chip>
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>
    

    <!-- Cards de Tipo de Projeto (Importação/Exportação) -->


    <!-- Toolbar de Status (filtrado pelo tipo selecionado) -->
    <div v-if="selectedProjectType !== null" class="mb-2">
      <div class="d-flex flex-wrap">
        <v-btn
          v-for="status in filteredStatusSummary"
          :key="status.id"
          :color="selectedStatus === status.id ? status.color : 'grey-lighten-4'"
          :variant="selectedStatus === status.id ? 'flat' : 'flat'"
          class="ma-1 text-none flex-grow-1"
          size="small"
          @click="selectStatus(status.id)"
          :disabled="status.count === 0"
          :elevation="selectedStatus === status.id ? 2 : 0"
          border
          style="min-width: 100px; min-height: 40px;"
        >
          <span class="font-weight-bold mr-2 text-h6">{{ status.count }}</span>
          <span class="mr-1 text-h6">{{ status.icon }}</span>
          <span class="mr-1">{{ status.descr }}</span>
          
          <v-badge v-if="status.warningCount > 0" :content="status.warningCount" color="orange" inline class="ml-1" style="transform: scale(1.2);"></v-badge>
          <v-badge v-if="status.overdueCount > 0" :content="status.overdueCount" color="red" inline class="ml-1" style="transform: scale(1.2);"></v-badge>
        </v-btn>
      </div>
    </div>

    

    <!-- Tabela de Projetos -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-format-list-bulleted</v-icon>
            {{ $t('projects.title') }}
          </v-card-title>

          <v-card-text>
            <ProjectsTable ref="projectsTable" @project-saved="onProjectSaved" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Actions FAB -->
    <v-btn icon="mdi-plus" color="primary" size="large" elevation="8" class="floating-fab"
      @click="openCreateProject"></v-btn>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import ProjectsTable from '../components/ProjectsTable.vue'

const projectStore = useProjectStore()
const projectsTable = ref()
const selectedProjectType = ref(null)
const selectedStatus = ref(null)
const isRefreshing = ref(false)

// Computed per riepilogo dei tipi di progetto (Importação/Exportação)
const projectTypeSummary = computed(() => {
  const typeCounts = {}
  
  // Inizializza contatori per tutti i tipi
  projectStore.projectTypes.forEach(type => {
    typeCounts[type.id] = 0
  })
  
  // Conta progetti per tipo - usa allProjects per avere tutti i progetti
  const projectsToCount = projectStore.allProjects.length > 0 ? projectStore.allProjects : projectStore.projects
  projectsToCount.forEach(project => {
    const typeId = parseInt(project.projectType)
    if (typeCounts.hasOwnProperty(typeId)) {
      typeCounts[typeId]++
    }
  })
  
  // Ritorna array con info dei tipi e contatori
  return projectStore.projectTypes.map(type => ({
    ...type,
    count: typeCounts[type.id] || 0,
    icon: type.id === 0 ? '📥' : '📤' // Importação / Exportação
  }))
})

// Label del tipo di progetto selezionato
const selectedProjectTypeLabel = computed(() => {
  if (selectedProjectType.value === null) return ''
  const type = projectStore.projectTypes.find(t => t.id === selectedProjectType.value)
  return type?.descr || ''
})

// Computed para resumo dos status (filtrato per tipo selezionado)
const filteredStatusSummary = computed(() => {
  if (selectedProjectType.value === null) return []
  
  const statusCounts = {}
  const warningFollowUpCounts = {}
  const overdueFollowUpCounts = {}
  
  // Get today's date in local timezone as string
  const today = new Date()
  const todayStr = today.getFullYear() + '-' + 
                   String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(today.getDate()).padStart(2, '0')
  
  // Ottieni la lista di status per il tipo selezionato
  const statusListForType = projectStore.getStatusListForType(selectedProjectType.value)
  
  // Inizializza contatori per tutti gli status del tipo selezionato
  statusListForType.forEach(status => {
    statusCounts[status.id] = 0
    warningFollowUpCounts[status.id] = 0
    overdueFollowUpCounts[status.id] = 0
  })
  
  // Conta progetti per status, filtrando per tipo selezionato - usa allProjects
  const projectsToCount = projectStore.allProjects.length > 0 ? projectStore.allProjects : projectStore.projects
  projectsToCount.forEach(project => {
    const projectTypeId = parseInt(project.projectType)
    const statusId = parseInt(project.activeStatus)
    
    // Conta solo se il tipo corrisponde
    if (projectTypeId === selectedProjectType.value) {
      if (statusCounts.hasOwnProperty(statusId)) {
        statusCounts[statusId]++
        
        // Verifica se il follow-up è oggi (warning) o scaduto (overdue)
        if (project.reminderDate) {
          const reminderStr = project.reminderDate.substring(0, 10)
          
          if (reminderStr === todayStr) {
            warningFollowUpCounts[statusId]++
          } else if (reminderStr < todayStr) {
            overdueFollowUpCounts[statusId]++
          }
        }
      }
    }
  })
  
  // Ritorna array con info degli status e contatori
  return statusListForType.map(status => ({
    ...status,
    count: statusCounts[status.id] || 0,
    warningCount: warningFollowUpCounts[status.id] || 0,
    overdueCount: overdueFollowUpCounts[status.id] || 0
  }))
})

// Watch to auto-deselect status if count becomes 0
watch(filteredStatusSummary, (newSummary) => {
  if (selectedStatus.value !== null) {
    const currentStatus = newSummary.find(s => s.id === selectedStatus.value)
    if (currentStatus && currentStatus.count === 0) {
      selectedStatus.value = null
      projectStore.setStatusFilter(null)
    }
  }
})

// Methods
const selectProjectType = (typeId) => {
  const normalizedTypeId = typeId !== undefined ? typeId : null
  selectedProjectType.value = normalizedTypeId
  selectedStatus.value = null // Reset status filter when changing type
  // Applica il filtro anche alla tabella tramite lo store
  projectStore.setProjectTypeFilter(normalizedTypeId)
  projectStore.setStatusFilter(null)
}

const selectStatus = (statusId) => {
  // Toggle: se clicco sullo stesso status, lo deseleziono
  if (selectedStatus.value === statusId) {
    selectedStatus.value = null
    projectStore.setStatusFilter(null)
  } else {
    selectedStatus.value = statusId
    projectStore.setStatusFilter(statusId)
  }
  // Non ricarica i dati, usa solo il filtro locale
}

const openCreateProject = () => {
  if (projectsTable.value) {
    projectsTable.value.openCreateDialog()
  }
}

const onProjectSaved = () => {
  // Atualizar dados quando um projeto for salvo (criado ou editado)
  // Mantém o filtro de status atual
  projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
}

// Função de refresh manual
const manualRefresh = async () => {
  isRefreshing.value = true
  try {
    console.log('Manual refresh triggered...')
    
    // Para o timer automático
    stopAutoRefresh()
    
    // Carrega os dados atualizados
    await projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
    
    // Reinicia o timer automático
    startAutoRefresh()
  } catch (error) {
    console.error('Error during manual refresh:', error)
  } finally {
    isRefreshing.value = false
  }
}

// Auto-refresh a cada 2 minutos
let refreshInterval = null

const startAutoRefresh = () => {
  // Atualiza os dados a cada 2 minutos (120000 ms)
  refreshInterval = setInterval(async () => {
    console.log('Auto-refreshing dashboard data silently...')
    // Usa refresh silenzioso per non disturbare l'utente
    await projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter, true)
  }, 120000) // 2 minutos
}

const stopAutoRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

// Watch per sincronizzare i card quando cambiano i filtri dalla tabella
watch(() => projectStore.statusFilter, (newStatusFilter) => {
  // Aggiorna il selectedStatus della dashboard quando cambia il filtro nello store
  selectedStatus.value = newStatusFilter
})

watch(() => projectStore.projectTypeFilter, (newTypeFilter) => {
  // Aggiorna il selectedProjectType della dashboard quando cambia il filtro nello store
  selectedProjectType.value = newTypeFilter
})

// Lifecycle
onMounted(async () => {
  // Sincronizza lo stato locale con lo store
  selectedProjectType.value = projectStore.projectTypeFilter
  selectedStatus.value = projectStore.statusFilter

  // Carregar configurações e projetos iniciais
  if (!projectStore.statusList.length) {
    await projectStore.loadConfigurations()
  }
  
  if (!projectStore.projects.length) {
    await projectStore.loadProjects()
  }
  
  // Inicia o auto-refresh
  startAutoRefresh()
  
  // Ascolta eventi di refresh dalla toolbar
  window.addEventListener('dashboard-refresh', manualRefresh)
})

onUnmounted(() => {
  // Limpa o intervalo quando o componente é desmontado
  stopAutoRefresh()
  
  // Remove listener de eventos
  window.removeEventListener('dashboard-refresh', manualRefresh)
})
</script>

<style scoped>
.dashboard {
  padding: 12px;
  max-width: 100%;
  margin: 0 auto;
}

.count-badge :deep(.v-badge__badge) {
  font-size: 1.1rem !important;
  min-width: 28px !important;
  height: 28px !important;
  padding: 0 8px !important;
  font-weight: bold !important;
}

.v-card {
  transition: transform 0.2s ease-in-out;
}

.v-card:hover {
  transform: translateY(-2px);
}

.cursor-pointer {
  cursor: pointer;
}

.text-h6 {
  font-weight: 600;
}

.floating-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}
</style>